import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSnapshot } from 'valtio';
import rootStore from '~/store';
import layerConfigStore, { canEditLayerConfig } from '~/store2/layerConfig';
import { availableLayersQueryKey } from './useAvailableLayers';

/**
 * Reactive view of the per-user layer-config load state for the picker UI. `retry` re-runs the
 * bootstrap for the active org (used by the failure affordance).
 */
export function useLayerConfigLoadState() {
  const { loadState } = useSnapshot(layerConfigStore);
  const { currentOrganizationId } = useSnapshot(rootStore) as typeof rootStore;
  const queryClient = useQueryClient();

  const retry = useCallback(() => {
    // The catalog is a react-query owned by useLayerConfigBootstrap; `bootstrap` only *awaits* it,
    // and its `staleTime` is Infinity, so a catalog failure never clears unless we refetch it here.
    // Without this, re-running bootstrap alone would re-await the same dead catalog and re-stall.
    if (currentOrganizationId) {
      void queryClient.refetchQueries({ queryKey: availableLayersQueryKey(currentOrganizationId) });
    }
    if (currentOrganizationId) void layerConfigStore.bootstrap(currentOrganizationId);
  }, [currentOrganizationId, queryClient]);

  return {
    loadState,
    isLoading: loadState === 'loading',
    hasFailed: loadState === 'failed',
    canEdit: canEditLayerConfig(loadState),
    retry,
  };
}
